import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGetMyProductLicensesQuery } from '@/store/api/apiSlice';
import { UserRole } from '@/types';
import type { ProductId } from '@/config/products';
import {
  AWAITING_ACCESS_PATH,
  getDefaultRouteForLaunchableProducts,
  isAllowedWithoutProductLicense,
  isDeliveryUserRole,
} from '@/utils/productLicense';
import {
  getRequiredProductForPath,
  isProductLicensedPath,
} from '@/utils/productLicensedPaths';

/**
 * Keeps users on product surfaces they are licensed for — redirects or blocks the rest.
 * On license API failure, allows navigation (soft-fail — do not trap on awaiting-access).
 */
export default function ProductAccessRoutingGuard({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role;

  const { data, isLoading, isError } = useGetMyProductLicensesQuery(undefined, {
    skip: !user || role === UserRole.SUPER_ADMIN,
  });

  if (!user || role === UserRole.SUPER_ADMIN) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg" />
    );
  }

  // Soft-fail: unknown license state → do not block or send to awaiting-access.
  if (isError) {
    return children;
  }

  const launchable = (data?.launchableProductIds ?? []) as ProductId[];
  const hasAnyLicense = launchable.length > 0;

  if (!hasAnyLicense) {
    if (isDeliveryUserRole(role) && isAllowedWithoutProductLicense(location.pathname)) {
      return children;
    }
    if (isDeliveryUserRole(role)) {
      return <Navigate to={AWAITING_ACCESS_PATH} replace />;
    }
    return children;
  }

  if (!isProductLicensedPath(location.pathname, location.search)) {
    return children;
  }

  const required = getRequiredProductForPath(location.pathname, location.search);
  if (launchable.includes(required)) {
    return children;
  }

  const fallback = getDefaultRouteForLaunchableProducts(
    launchable,
    role,
    user?.tenant?.settings as Record<string, unknown> | undefined,
  );

  if (location.pathname === fallback.split('?')[0]) {
    return children;
  }

  return <Navigate to={fallback} replace />;
}
